/* eslint-disable camelcase */
import {
  MONGO_BACKUP_LOCATIONS,
  DATABASE_TYPES,
  DAYS,
  INSTANCE_OPTIONS_BY_EDITION,
  MONGO_DB_TYPES,
  MONGODB_EDITIONS, MONGODB_ENTERPRISE_INSTANCE_OPTIONS,
  MONGODB_INSTANCE_OPTIONS,
  MONGODB_LOCATIONS,
  STORAGE_TYPES,
  VALIDATION,
  AGP_MONGO_BACKUP_LOCATIONS,
} from 'constant';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { actions } from 'redux/actions';
import { ACTIONS_TYPES } from 'redux/helper';
import Shared from 'screens/shared';
import {
  _t, toInt, getKey, isSsdStorageType, enumToArray,
  getSessionKey,
} from 'utils';
import { getResourceLimitsForContract } from 'utils/contract';
import { getPossibleInstancesOptionsForTemplate, sortAndGenerateTemplates } from 'utils/database';
import { getDatacenterName } from 'utils/datacenter';
import { getConfig } from 'utils/config';

export default ({ onCancel }) => {
  const [model, setModel] = useState({});
  const [changeFields, setChangeFields] = useState([]);
  const [playgroundInstanceExist, setPlayGroundInstanceExist] = useState(false);

  const {
    [ACTIONS_TYPES.DATABASE.MONGODB.GET_ALL_VERSIONS]: versions = ['5.0', '6.0'],
    [ACTIONS_TYPES.DATABASE.MONGODB.GET_TEMPLATES]: templates = [],
    // [ACTIONS_TYPES.COMMON.BUFFER_CLONE_DATA]: cloneData,
    [ACTIONS_TYPES.CONTRACT.GET_ALL]: contracts = [],
    [ACTIONS_TYPES.DATA_CENTER.GET_ALL]: dataCenters = [],
    [ACTIONS_TYPES.DATABASE.GET_QUOTA]: dbQuota = {},
    [ACTIONS_TYPES.DATABASE.MONGODB.GET_ALL]: databaseClusters = [],
  } = useSelector((e) => e);

  const validLocations = [...MONGODB_LOCATIONS];
  const { envName = '' } = getConfig() || {};
  if (['stage', 'dev'].includes(envName)) {
    validLocations.push('de/fkb');
  }

  const {
    quotaUsage = {},
    quotaLimits = {},
  } = dbQuota || {};

  let maxCores = toInt(quotaLimits?.cpu, 8) - ((toInt(quotaUsage?.cpu)) || 0);
  let maxRam = toInt(quotaLimits?.memory, 16) - ((toInt(quotaUsage?.memory)) || 0);
  let maxStorage = toInt(quotaLimits?.storage, 150) - ((toInt(quotaUsage?.storage)) || 0);

  maxCores = maxCores < 0 ? 0 : maxCores;
  maxRam = maxRam < 0 ? 0 : maxRam;
  maxStorage = maxStorage < 0 ? 0 : maxStorage;

  const resourceLimits = getResourceLimitsForContract(contracts);

  const brandId = getSessionKey('brandId');

  const checkPlaygroundInstanceExist = () => {
    const playgroundCluster = databaseClusters.filter(({ properties }) => {
      const template = templates.find((e) => e.id === properties.templateID);
      return template?.properties?.edition === MONGODB_EDITIONS.PLAYGROUND;
    });
    setPlayGroundInstanceExist(playgroundCluster.length > 0);
  };

  const {
    coresPerContract = maxCores,
    ramPerContract = (maxRam * 1024),
  } = resourceLimits;
  const maxContractRam = ramPerContract / 1024;
  if (coresPerContract < maxCores) {
    maxCores = coresPerContract;
  }
  if (maxContractRam < maxRam) {
    maxRam = maxContractRam;
  }

  const setCurrentModel = (formValues) => {
    const output = { ...formValues };

    if (model.edition !== output.edition) {
      delete output.templateID;
    }
    if (output.edition === MONGODB_EDITIONS.PLAYGROUND) {
      output.instances = Math.min(...MONGODB_INSTANCE_OPTIONS);
    }

    if (output.edition !== model.edition) {
      if (output.edition === MONGODB_EDITIONS.ENTERPRISE) {
        output.ram = 2;
        output.cores = 2;
        output.storageSize = 100;
        output.storageType = STORAGE_TYPES.SSD_PREMIUM;
        output.instances = Math.min(...MONGODB_ENTERPRISE_INSTANCE_OPTIONS);
      } else {
        delete output.ram;
        delete output.cores;
        delete output.storageSize;
        delete output.storageType;
      }
    }

    setModel(output);
    return output;
  };

  const setModelData = () => {
    const localModel = {
      mongoDBVersion: '',
      instances: Math.min(...MONGODB_INSTANCE_OPTIONS),
      type: MONGO_DB_TYPES.REPLICASET,
    };
    // if (cloneData) {
    //   localModel = {
    //     ...cloneData,
    //     displayName: `${cloneData.displayName} - Copy`,
    //     ram: cloneData.ram / 1024,
    //     storageSize: cloneData.storageSize / 1024,
    //     connections: [],
    //     ...(backup ? { fromBackup: { backupId: backup.id, recoveryTargetTime: null } } : {}),
    //   };
    //
    //   delete localModel.id;
    // }
    setCurrentModel(localModel);
    return localModel;
  };

  const connectionsValidation = (input) => [
    {
      name: 'datacenterId',
      type: VALIDATION.REQUIRED,
    },
    {
      name: 'lanId',
      type: VALIDATION.REQUIRED,
    },
    {
      name: 'lanId',
      type: VALIDATION.NUMBER,
      min: 1,
      max: 50,
    },
    {
      name: 'cidrList',
      type: VALIDATION.ARRAY,
      min: input.instances || 1,
      max: input.instances || 1,
      message: _t('DATABASES.FORM_FIELDS.CONNECTIONS.FIELDS.CIDR_LIST.LENGTH_VALIDATION'),
    },
    // {
    //   name: 'whitelist',
    //   type: VALIDATION.ARRAY,
    // },
  ];

  const enterpriseInputs = [{
    name: 'cores',
    type: 'slider',
    showValue: true,
    instantChange: false,
    title: _t('DATABASES.FORM_FIELDS.CORES.TITLE'),
    range: [1, maxCores > 8 ? maxCores : 8],
    options: [4, 8].filter((item) => item <= maxCores),
    hidden: model.edition !== MONGODB_EDITIONS.ENTERPRISE,
  },
  {
    name: 'ram',
    type: 'slider',
    showValue: true,
    instantChange: false,
    title: _t('DATABASES.FORM_FIELDS.RAM.TITLE'),
    range: [2, maxRam > 16 ? maxRam : 16],
    options: [4, 8, 16].filter((item) => item <= maxRam),
    hidden: model.edition !== MONGODB_EDITIONS.ENTERPRISE,
  },
  {
    name: 'storageType',
    type: 'dropdown',
    title: _t('DATABASES.FORM_FIELDS.STORAGE_TYPE.TITLE'),
    options: [STORAGE_TYPES.SSD_PREMIUM, STORAGE_TYPES.SSD_STANDARD, STORAGE_TYPES.HDD],
    hidden: model.edition !== MONGODB_EDITIONS.ENTERPRISE,
  },
  {
    name: 'storageSize',
    type: 'number',
    isNumber: true,
    title: _t('DATABASES.FORM_FIELDS.STORAGE_SIZE.TITLE'),
    step: '1',
    stepIsMandatory: true,
    instantChange: false,
    min: 0,
    suffix: 'GB',
    hidden: model.edition !== MONGODB_EDITIONS.ENTERPRISE,
    helpText: isSsdStorageType(model.storageType || '') ? _t('DATABASES.FORM_FIELDS.STORAGE_SIZE.SSD_INFO') : '',
    // max: Math.floor(maxStorage / model.instances + model.storageSize),
  }];

  const backupInputs = {
    name: 'backup',
    type: 'object',
    title: 'Backup',
    hidden: model.edition !== MONGODB_EDITIONS.ENTERPRISE,
    fields: [
      {
        name: 'location',
        type: 'dropdown',
        title: _t('DATABASES.FORM_FIELDS.BACKUP_LOCATION.TITLE'),
        options: enumToArray(brandId === 'itzbund' ? AGP_MONGO_BACKUP_LOCATIONS : MONGO_BACKUP_LOCATIONS).map((e) => [e, e]),
        // hidden: model.edition === MONGODB_EDITIONS.PLAYGROUND,
      },
    ],
  };

  const computeChangeFields = (props) => {
    const currentModel = props || model;
    const isEnterprise = currentModel.edition === MONGODB_EDITIONS.ENTERPRISE;
    const templateOptions = sortAndGenerateTemplates(templates, currentModel, {
      maxCores,
      maxRam,
      maxStorage,
    });
    const instanceOptions = getPossibleInstancesOptionsForTemplate({
      templates,
      model: currentModel,
      quota: {
        maxCores,
        maxRam,
        maxStorage,
      },
      instanceOptions: INSTANCE_OPTIONS_BY_EDITION[currentModel.edition],
    });

    let hints = [];

    if (!isEnterprise) {
      hints = [
        {
          name: 'templateID',
          props: {
            options: templateOptions,
            disabled: false,
            externalError: '',
            hidden: false,
          },
        },
      ];

      if (templateOptions.length === 0) {
        hints = [{
          name: 'templateID',
          props: {
            externalError: _t('DATABASES.MONGODB.OUT_OF_TEMPLATES'),
            options: [],
            disabled: false,
            hidden: false,
          },
        }];
      }

      enterpriseInputs.forEach((input) => {
        hints.push({
          name: input.name,
          props: {
            hidden: true,
          },
        });
      });

      hints.push({
        name: 'location',
        props: {
          validLocations,
        },
      });
    } else {
      const maxRamAndCurrentRam = maxRam - (currentModel.ram || 0) * currentModel.instances * (currentModel.shards || 1);
      const maxCoresAndCurrentCores = maxCores - (currentModel.cores || 0) * currentModel.instances * (currentModel.shards || 1);

      const ramOptions = [4, 8, 16].filter((e) => e < maxRamAndCurrentRam);
      const coreOptions = [4, 8].filter((e) => e < maxCoresAndCurrentCores);
      const maxStorageLimit = Math.floor((maxStorage / (currentModel.instances * (currentModel.shards || 1))) - (currentModel.storageSize || 0));

      hints = [
        {
          name: 'templateID',
          props: {
            hidden: true,
          },
        },
        {
          name: 'storageType',
          props: {
            hidden: false,
          },
        },
        {
          name: 'cores',
          props: {
            hidden: false,
            externalError: maxCoresAndCurrentCores < 0 ? 'You already consumed the maximum limit' : '',
            range: [1, maxCoresAndCurrentCores > 8 ? maxCoresAndCurrentCores : 8],
            options: coreOptions,
          },
        },
        {
          name: 'ram',
          props: {
            hidden: false,
            externalError: maxRamAndCurrentRam < 0 ? 'You already consumed the maximum limit' : '',
            range: [2, maxRamAndCurrentRam > 16 ? maxRamAndCurrentRam : 16],
            options: ramOptions,
          },
        },
        {
          name: 'storageSize',
          props: {
            hidden: false,
            externalError: maxStorageLimit < 0 ? `You already consumed the maximum limit. You have only ${maxStorage} GB left.` : '',
            helpText: isSsdStorageType(currentModel.storageType || '') ? _t('DATABASES.FORM_FIELDS.STORAGE_SIZE.SSD_INFO') : '',
          },
        },
        {
          name: 'location',
          props: {
            validLocations: null,
          },
        },
      ];
    }

    hints.push({
      name: 'backup',
      props: {
        hidden: !isEnterprise,
      },
    });

    hints.push({
      name: 'datacenterId',
      props: {
        validLocations: currentModel.location ? [currentModel.location] : null,
      },
    });

    if (!currentModel.edition) {
      hints = [{
        name: 'templateID',
        props: {
          disabled: true,
        },
      }];
    }

    const currentTemplate = templates.find((e) => e.id === currentModel.templateID) || {};
    setChangeFields([
      ...hints,
      {
        name: 'mongoDBVersion',
        props: { options: versions },
      },
      {
        name: 'cidrList',
        props: { elementCount: currentModel.instances },
      },
      {
        name: 'connections',
        props: { validations: connectionsValidation(currentModel) },
      },
      {
        name: 'biConnector',
        props: { hidden: !isEnterprise },
      },
      {
        name: 'type',
        props: {
          options: [
            [MONGO_DB_TYPES.REPLICASET, _t('DATABASES.DB_TYPES.REPLICASET')],
            [MONGO_DB_TYPES.SHARDED_CLUSTER, _t('DATABASES.DB_TYPES.SHARDED_CLUSTER'), null, !isEnterprise],
          ],
        },
      },
      {
        name: 'shards',
        props: {
          hidden: currentModel.type !== MONGO_DB_TYPES.SHARDED_CLUSTER,
        },
      },
      {
        name: 'instances',
        props: {
          options: instanceOptions,
          element: (
            <Shared.billing.DBaaSPriceEstimation
              freeOfCharge={currentTemplate?.properties?.edition === MONGODB_EDITIONS.PLAYGROUND && !playgroundInstanceExist}
              model={{
                ...currentModel,
                templateID: currentTemplate?.id,
              }}
              databaseType={DATABASE_TYPES.MONGODB}
            />
          ),
        },
      },
    ]);
  };

  useEffect(() => {
    if (versions) {
      computeChangeFields(setModelData());
      checkPlaygroundInstanceExist();
    }
    // eslint-disable-next-line
  }, [versions.length, templates.length]);

  useEffect(() => {
    // actions.database.mongodb.getAllVersions();
    actions.database.mongodb.getTemplates();
    actions.contract.getAll();
  }, []);

  const isEnterprise = model.edition === MONGODB_EDITIONS.ENTERPRISE;

  const fields = [
    {
      name: 'displayName',
      type: 'text',
      title: _t('DATABASES.FORM_FIELDS.DISPLAY_NAME.TITLE'),
    },
    {
      name: 'edition',
      type: 'dropdown',
      title: 'Edition', // _t('DATABASES.FORM_FIELDS.PRODUCT.TITLE'),
      options: [
        [MONGODB_EDITIONS.PLAYGROUND, 'Playground', _t('DATABASES.MONGODB.EDITIONS.PLAYGROUND.DESCRIPTION')],
        [MONGODB_EDITIONS.BUSINESS, 'Business'],
        [MONGODB_EDITIONS.ENTERPRISE, 'Enterprise'],
      ],
    },
    {
      name: 'location',
      type: 'location',
      ...(isEnterprise ? {} : { validLocations }),
      title: _t('DATABASES.FORM_FIELDS.LOCATION.TITLE'),
    },
    ...enterpriseInputs,
    {
      name: 'templateID',
      className: 'titleDropdown',
      multiline: true,
      type: 'dropdown',
      options: [],
      title: _t('DATABASES.PROPERTIES.TEMPLATE'),
      hidden: model.edition === MONGODB_EDITIONS.ENTERPRISE,
    },
    {
      name: 'type',
      type: 'dropdown',
      title: _t('DATABASES.DB_TYPES'),
      options: [
        [MONGO_DB_TYPES.REPLICASET, _t('DATABASES.DB_TYPES.REPLICASET')],
        [MONGO_DB_TYPES.SHARDED_CLUSTER, _t('DATABASES.DB_TYPES.SHARDED_CLUSTER'), null, !isEnterprise],
      ],
    },
    {
      name: 'shards',
      instantChange: false,
      type: 'number',
      title: _t('DATABASES.PROPERTIES.SHARDS'),
      min: 2,
      max: 32,
      hidden: true,
    },
    {
      name: 'instances',
      type: 'dropdown',
      title: _t('DATABASES.FORM_FIELDS.INSTANCES.TITLE'),
      isNumber: true,
      options: MONGODB_INSTANCE_OPTIONS,
      // helpText: _t('DATABASES.FORM_FIELDS.INSTANCES.HELP_TEXT'),
      onChange: () => setModel({ ...model }),
    },
    {
      name: 'connections',
      type: 'array',
      icon: 'boxes',
      title: _t('DATABASES.FORM_FIELDS.CONNECTIONS.TITLE'),
      // subTitle: 'Multiple connections can be added', // currently only 1 connection supported
      actionButtonName: _t('DATABASES.FORM_FIELDS.CONNECTIONS.ACTION_BUTTON'),
      maxItems: 1,
      editable: true,
      setItemTitle: (con) => getDatacenterName(con.datacenterId, dataCenters),
      setItemDetails: (con) => `LAN: ${con.lanId}${con.cidrList ? `, ${con.cidrList.length} IP(s)` : ''},
        // ${con.whitelist ? `, ${con.whitelist.length} Whitelisted IP(s)` : ''}`,
      updateProps: (props) => {
        const { location } = props;
        const _fields = fields.find((e) => e.name === 'connections').fields;
        _fields.find((e) => e.name === 'datacenterId').location = location;
        return { fields: _fields };
      },
      fields: [
        {
          name: 'datacenterId',
          type: 'datacenter',
          ...(isEnterprise ? {} : { validLocations }),
          title: _t('DATABASES.FORM_FIELDS.CONNECTIONS.FIELDS.DATACENTER.TITLE'),
          setValueFrom: (e) => {
            actions.common.quickUpdate({ [ACTIONS_TYPES.DATA_CENTER.GET]: e });
            return e.id;
          },
        },
        {
          name: 'lanId',
          type: 'lan',
          hasNewOption: false,
          privateOnly: true,
          title: _t('DATABASES.FORM_FIELDS.CONNECTIONS.FIELDS.LAN_ID.TITLE'),
          updateProps: (values) => ({ disabled: !values.datacenterId }),
        },
        {
          name: 'cidrList',
          type: 'array',
          title: _t('DATABASES.FORM_FIELDS.CONNECTIONS.FIELDS.CIDR_LIST.TITLE'),
          setItemTitle: (list) => `IP: ${list.cidr}`,
          setValueFrom: (e) => e.map(({ cidr }) => cidr),
          setValueTo: (e = []) => e.map((cidr) => ({ cidr })),
          elementCount: model.instances || 1,
          uniqueValues: true,
          fields: [
            {
              name: 'cidr',
              type: 'text',
              title: _t('DATABASES.FORM_FIELDS.CONNECTIONS.FIELDS.PRIVATE_IP.TITLE'),
              helpText: (
                <span>
                  {_t('DATABASES.FORM_FIELDS.CONNECTIONS.FIELDS.PRIVATE_IP.HELP_TEXT.TEXT')}
                  &nbsp;
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://docs.ionos.com/cloud/managed-services/database-as-a-service/mongodb/dcd-how-tos/set-up-mongodb-cluster/"
                  >
                    {_t('DATABASES.FORM_FIELDS.CONNECTIONS.FIELDS.PRIVATE_IP.HELP_TEXT.HREF')}
                  </a>
                </span>
              ),
            },
          ],
          validations: [
            {
              name: 'cidr',
              type: VALIDATION.REQUIRED,
            },
            {
              name: 'cidr',
              type: VALIDATION.PRIVATE_IP_SUBNET,
            },
          ],
        },
        // {
        //   name: 'whitelist',
        //   type: 'array',
        //   title: _t('DATABASES.FORM_FIELDS.CONNECTIONS.FIELDS.WHITE_LIST.TITLE'),
        //   setItemTitle: (list) => `IP: ${list.cidr}`,
        //   setValueFrom: (e) => e.map(({ cidr }) => cidr),
        //   setValueTo: (e = []) => e.map((cidr) => ({ cidr })),
        //   uniqueValues: true,
        //   fields: [
        //     {
        //       name: 'cidr',
        //       type: 'text',
        //       title: _t('DATABASES.FORM_FIELDS.CONNECTIONS.FIELDS.PRIVATE_IP.TITLE'),
        //       helpText: (
        //         <span>
        //           {_t('DATABASES.FORM_FIELDS.CONNECTIONS.FIELDS.PRIVATE_IP.HELP_TEXT.TEXT')}
        //           &nbsp;
        //           <a
        //             target="_blank"
        //             rel="noopener noreferrer"
        //             href="https://www.ionos.com/help/server-cloud-infrastructure/private-network/private-ip-address-ranges/"
        //           >
        //             {_t('DATABASES.FORM_FIELDS.CONNECTIONS.FIELDS.PRIVATE_IP.HELP_TEXT.HREF')}
        //           </a>
        //         </span>
        //       ),
        //     },
        //   ],
        //   validations: [
        //     { name: 'cidr', type: VALIDATION.PRIVATE_IP_SUBNET },
        //   ],
        // },
      ],
      validations: connectionsValidation(model),
    },
    {
      name: 'mongoDBVersion',
      type: 'dropdown',
      options: [],
      title: _t('DATABASES.FORM_FIELDS.MONGODB_VERSION.TITLE'),
    },
    backupInputs,
    {
      name: 'biConnector',
      type: 'object',
      title: _t('DATABASES.FORM_FIELDS.MONGODB_BI_CONNECTOR.TITLE'),
      hidden: !isEnterprise,
      fields: [
        {
          name: 'enabled',
          title: _t('DATABASES.FORM_FIELDS.MONGODB_BI_CONNECTOR.ENABLE'),
          type: 'switch',
        },
      ],
    },
    {
      name: 'maintenanceWindow',
      type: 'object',
      title: _t('DATABASES.FORM_FIELDS.MAINTENANCE_WINDOW.TITLE'),
      subTitle: _t('DATABASES.FORM_FIELDS.MAINTENANCE_WINDOW.SUBTITLE'),
      fields: [
        {
          name: 'time',
          type: 'time',
          title: _t('Maintenance time'),
        },
        {
          name: 'dayOfTheWeek',
          type: 'dropdown',
          title: _t('Maintenance day'),
          options: DAYS,
        },
      ],
    },
  ];

  const validations = [
    {
      name: 'displayName',
      type: VALIDATION.REQUIRED,
    },
    {
      name: 'displayName',
      type: VALIDATION.STRING,
      max: 256,
    },
    {
      name: 'location',
      type: VALIDATION.REQUIRED,
    },
    {
      name: 'mongoDBVersion',
      type: VALIDATION.REQUIRED,
    },
    {
      name: 'instances',
      type: VALIDATION.REQUIRED,
    },
    {
      name: 'connections',
      type: VALIDATION.REQUIRED,
    },
    ...(model.type === MONGO_DB_TYPES.SHARDED_CLUSTER ? [
      {
        name: 'shards',
        type: VALIDATION.NUMBER,
        min: 2,
        max: 32,
      },
    ] : []),
    ...(model.edition === MONGODB_EDITIONS.ENTERPRISE ? [
      {
        name: 'ram',
        type: VALIDATION.REQUIRED,
      },
      {
        name: 'cores',
        type: VALIDATION.REQUIRED,
      },
      {
        name: 'storageSize',
        type: VALIDATION.REQUIRED,
      },
    ] : [
      {
        name: 'templateID',
        type: VALIDATION.REQUIRED,
      },
    ]),
  ];

  const submitHandler = (values) => {
    if (values.biConnector === undefined && values.edition === MONGODB_EDITIONS.ENTERPRISE) {
      values.biConnector = {
        enabled: false,
      };
    }

    if (values.ram) {
      values.ram *= 1024;
    }
    if (values.storageSize) {
      values.storageSize *= 1024;
    }

    actions.database.mongodb.create({ properties: values })
      .then((cluster) => {
        if (cluster?.id && getKey('isAccessByDCD')) {
          window.top.postMessage({
            event: 'onClusterCreate',
            id: cluster.id,
            database: 'mongodb',
          }, '*');
        }
      });
  };

  return (
    <Shared.forms.Form
      fields={fields}
      fieldsValues={model}
      onProps={model}
      validations={validations}
      onSubmit={submitHandler}
      changeFields={changeFields}
      onChange={(formValues) => {
        const values = setCurrentModel(formValues);
        computeChangeFields(values);
      }}
      onCancel={() => onCancel()}
      isCancel
      validateOnBlur
    />
  );
};
